/**
 * Back-in-stock drawer.
 *
 * Klaviyo's back-in-stock form has no product block — its editor offers text,
 * inputs, a consent checkbox and a static image, nothing variant-aware. The
 * product data it captures (image, variant, price) only exists on the
 * "Subscribed to Back in Stock" event, i.e. after submit, for the email flow.
 * So the copy and the subscribe mechanics stay in Klaviyo, and the theme adds
 * the two things it can't: the drawer shell and the product panel.
 *
 * Nothing here targets Klaviyo's generated class names (go3176171171,
 * kl-private-reset-css-Xuajs1) — they change every time the form is
 * republished. We find the form, tag its overlay with our own classes, and all
 * styling hangs off those.
 *
 * Everything is defensive: if a piece of product data or a wrapper is missing
 * we skip that bit rather than throwing, because a broken script here would
 * take the subscribe form down with it.
 */
(function () {
  // The product section re-renders on variant change and re-runs its scripts.
  // Without this guard a second copy of the back-in-stock drawer binds its own document
  // listeners, and the two cancel each other out on alternate interactions.
  if (window.__weldunBisDrawerLoaded) return;
  window.__weldunBisDrawerLoaded = true;

  var FORM = 'form.klaviyo-form';
  var OVERLAY_CLASS = 'bis-drawer';
  var PANEL_CLASS = 'bis-drawer__panel';
  var PRODUCT_CLASS = 'bis-drawer__product';

  function text(el) {
    return el ? (el.textContent || '').trim() : '';
  }

  /* ------------------------------------------------- current variant lookup */

  // Read what the shopper actually has selected, so the panel can never show a
  // different variant from the one they asked to be notified about.
  function readProduct() {
    var info = document.querySelector('product-info, .product__info-container') || document;

    var data = { image: '', vendor: '', title: '', price: '', options: [] };

    var heading = info.querySelector('.product__heading, .product__title h1, h1');
    data.title = text(heading);

    var vendor = info.querySelector('.product__vendor, .product__title .caption-with-letter-spacing, .card-article-info');
    data.vendor = text(vendor);

    var price = info.querySelector('.price__sale .price-item--sale, .price__regular .price-item--regular, .price-item');
    data.price = text(price);

    // Selected option values, in the order the form presents them.
    info.querySelectorAll('.product-form__input').forEach(function (fieldset) {
      var checked = fieldset.querySelector('input[type="radio"]:checked');
      var value = '';
      if (checked) value = checked.value;
      else {
        var select = fieldset.querySelector('select');
        if (select) value = select.value;
      }
      if (value && data.options.indexOf(value) === -1) data.options.push(value);
    });

    data.image = findVariantImage();

    return data;
  }

  // The theme tags each gallery item with .is-active for the selected colour
  // (the same grouping its own colour filter uses), so the first active item is
  // the image for the variant in hand. Alt text is not usable here: the theme
  // strips the "| <colour>" suffix it groups on before rendering.
  function findVariantImage() {
    var active = document.querySelector('.product__media-item.is-active img');
    if (!active) active = document.querySelector('.product__media-item img');
    if (!active) return '';
    return active.currentSrc || active.src || '';
  }

  function buildProductPanel() {
    var data = readProduct();
    if (!data.title && !data.image) return null;

    var wrap = document.createElement('div');
    wrap.className = PRODUCT_CLASS;

    var html = '';
    if (data.image) {
      html +=
        '<div class="bis-drawer__media"><img src="' +
        data.image.replace(/"/g, '&quot;') +
        '" alt="" loading="lazy"></div>';
    }
    html += '<div class="bis-drawer__details">';
    if (data.vendor) html += '<p class="bis-drawer__vendor">' + escapeHtml(data.vendor) + '</p>';
    if (data.title) html += '<p class="bis-drawer__title">' + escapeHtml(data.title) + '</p>';
    if (data.price) html += '<p class="bis-drawer__price">' + escapeHtml(data.price) + '</p>';
    if (data.options.length) {
      html += '<ul class="bis-drawer__options list-unstyled">';
      data.options.forEach(function (option) {
        html += '<li>' + escapeHtml(option) + '</li>';
      });
      html += '</ul>';
    }
    html += '</div>';

    wrap.innerHTML = html;
    return wrap;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* --------------------------------------------------------------- plumbing */

  // Klaviyo nests the form several divs deep inside a fixed overlay. Walk up to
  // that overlay so the drawer geometry has something to hang off.
  function findOverlay(form) {
    var node = form;
    while (node && node !== document.body) {
      if (getComputedStyle(node).position === 'fixed') return node;
      node = node.parentElement;
    }
    return null;
  }

  // The panel is the outermost block inside the overlay — the visible card.
  function findPanel(overlay, form) {
    var node = form;
    while (node && node.parentElement && node.parentElement !== overlay) node = node.parentElement;
    return node || form;
  }

  // Klaviyo sets an inline transform: scale(...) on the panel to fit its form
  // to the viewport. The drawer sizes itself, and that inline value would beat
  // the stylesheet — so it is removed here (and again if Klaviyo re-applies it,
  // which it does on resize) rather than fought with !important.
  function stripInlineTransform(panel) {
    panel.style.removeProperty('transform');
    if (panel.dataset.bisTransformWatch === 'true') return;
    panel.dataset.bisTransformWatch = 'true';

    new MutationObserver(function () {
      if (panel.dataset.bisDriving === 'true') return; // closeDrawer is steering
      if (panel.style.transform) panel.style.removeProperty('transform');
    }).observe(panel, { attributes: true, attributeFilter: ['style'] });
  }

  function addBackButton(panel) {
    if (panel.querySelector('.bis-drawer__back')) return;
    var back = document.createElement('button');
    back.type = 'button';
    back.className = 'bis-drawer__back';
    back.innerHTML =
      '<svg width="7" height="12" viewBox="0 0 7 12" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M6 1L1 6L6 11" stroke="currentColor"/></svg><span>Back</span>';
    back.addEventListener('click', function () {
      closeDrawer(panel.closest('.' + OVERLAY_CLASS), findClose(panel, back));
    });
    panel.insertBefore(back, panel.firstChild);
  }

  // Klaviyo's own close button, whatever it is called this week.
  function findClose(panel, exclude) {
    var candidates = panel.querySelectorAll('button[aria-label*="lose" i], [class*="close"]');
    for (var i = 0; i < candidates.length; i += 1) {
      if (candidates[i] !== exclude && !candidates[i].classList.contains('bis-drawer__back')) {
        return candidates[i];
      }
    }
    return null;
  }

  function slideDuration() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue('--duration-long');
    var ms = parseFloat(raw);
    if (!ms) return 500;
    return raw.indexOf('ms') !== -1 ? ms : ms * 1000;
  }

  // Klaviyo removes the form the moment its close fires, which would make the
  // panel vanish rather than slide away. So we take the first click, run the
  // slide-out, then hand the click back to Klaviyo.
  function closeDrawer(overlay, realClose) {
    if (!overlay || overlay.dataset.bisClosing === 'true') return;
    overlay.dataset.bisClosing = 'true';

    // The open keyframe holds the panel at translateX(0) with animation-fill.
    // Dropping .is-open removes the animation, and without a committed start
    // value the panel would jump straight to the closed position instead of
    // sliding. Pin where it actually is, commit it, then let the stylesheet
    // transition it out.
    var panel = overlay.querySelector('.' + PANEL_CLASS);
    if (panel) {
      panel.dataset.bisDriving = 'true';
      panel.style.setProperty('transform', 'translateX(0)');
      void panel.getBoundingClientRect();
    }

    overlay.classList.remove('is-open');

    if (panel) {
      requestAnimationFrame(function () {
        panel.style.setProperty('transform', 'translateX(100%)');
      });
    }

    setTimeout(function () {
      overlay.dataset.bisAllowClose = 'true';
      if (realClose) realClose.click();
      else overlay.remove();

      // Klaviyo locks the page with body.klaviyo-prevent-body-scrolling and
      // clears it in its own close handler — which does not run for a backdrop
      // or replayed close, leaving the page unscrollable. Release it once the
      // form has actually gone. Only Klaviyo's own class is touched, so the
      // theme's drawer scroll locks are left alone.
      setTimeout(function () {
        // Gated on the drawer, not on the form: Klaviyo keeps the form node in
        // the DOM between opens, so "is the form gone" is never true.
        if (!document.querySelector('.' + OVERLAY_CLASS + '.is-open')) {
          document.body.classList.remove('klaviyo-prevent-body-scrolling');
        }
      }, 80);
    }, slideDuration());
  }

  function bindCloseIntercept(overlay, panel) {
    if (overlay.dataset.bisCloseBound === 'true') return;
    overlay.dataset.bisCloseBound = 'true';

    overlay.addEventListener(
      'click',
      function (event) {
        if (overlay.dataset.bisAllowClose === 'true') return; // our own replay
        var target = event.target;
        if (!target || !target.closest) return;

        var trigger = target.closest('button[aria-label*="lose" i], [class*="close"]');
        if (trigger && trigger.classList.contains('bis-drawer__back')) return;

        // A click on the backdrop dismisses too, and has to animate the same
        // way as the close button.
        var onBackdrop = !trigger && !panel.contains(target);
        if (!trigger && !onBackdrop) return;

        event.preventDefault();
        event.stopPropagation();
        closeDrawer(overlay, trigger || target);
      },
      true
    );

    // Escape takes the same path.
    var onKey = function (event) {
      if (event.key !== 'Escape' && event.keyCode !== 27) return;
      if (!document.contains(overlay)) {
        document.removeEventListener('keydown', onKey, true);
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      closeDrawer(overlay, findClose(panel, null));
    };
    document.addEventListener('keydown', onKey, true);
  }

  function decorate(form) {
    if (form.dataset.bisDrawer === 'true') return;

    var overlay = findOverlay(form);
    if (!overlay) return; // not mounted yet — the observer will call us again
    form.dataset.bisDrawer = 'true';

    overlay.classList.add(OVERLAY_CLASS);

    var panel = findPanel(overlay, form);
    panel.classList.add(PANEL_CLASS);

    addBackButton(panel);

    refreshProductPanel(form);
    bindCloseIntercept(overlay, panel);
  }

  // Rebuilt on every open, never cached. Klaviyo reuses the same form node for
  // later opens, so a panel built once would keep showing whichever variant was
  // selected the first time — and appending to it would double the option list.
  function refreshProductPanel(form) {
    if (!form) return;
    form.querySelectorAll('.' + PRODUCT_CLASS).forEach(function (node) {
      node.remove();
    });
    var product = buildProductPanel();
    if (product) form.insertBefore(product, form.firstChild);
  }

  // Klaviyo keeps the overlay mounted and hidden behind its own opacity, so its
  // computed style never tells us cleanly when the drawer opens. The button
  // click does, and it is the only way in.
  function openOnTrigger() {
    document.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        // The theme's own trigger opens the same form via klaviyo.openForm, so
        // it needs the drawer opened too or the form mounts invisibly and
        // locks page scroll.
        if (!target.closest('#klaviyo-bis-button-container, .klaviyo-bis-trigger')) return;
        awaitPanelThenOpen();
      },
      true
    );
  }

  function awaitPanelThenOpen() {
    var tries = 0;
    var poll = setInterval(function () {
      tries += 1;
      var form = document.querySelector(FORM);
      var overlay = form && form.closest('.' + OVERLAY_CLASS);
      if (overlay) {
        clearInterval(poll);
        delete overlay.dataset.bisClosing;
        delete overlay.dataset.bisAllowClose;

        // Klaviyo builds the form fresh on each open, so the panel has never
        // rendered a frame in the closed position and the transition has
        // nothing to run from. Pin the closed transform inline, force a layout
        // read so the browser commits it, then hand back to the stylesheet on
        // the next frame — that gives the transition a real starting value.
        refreshProductPanel(form);

        var panel = overlay.querySelector('.' + PANEL_CLASS);
        if (panel) stripInlineTransform(panel);

        requestAnimationFrame(function () {
          overlay.classList.add('is-open');
        });
        return;
      }
      // The theme trigger waits up to 12s for Klaviyo's bundle before calling
      // openForm (klaviyo-bis-placement.js), so wait at least as long here.
      if (tries > 700) clearInterval(poll);
    }, 20);
  }

  // The stylesheet keeps an undecorated form invisible so Klaviyo's default
  // modal never flashes before the drawer is set up. If decoration somehow
  // never happens, reveal the form anyway — a plain Klaviyo modal is a far
  // better outcome than a blank screen.
  function failSafe(form) {
    if (form.dataset.bisReveal === 'true') return;
    form.dataset.bisReveal = 'true';
    setTimeout(function () {
      if (form.dataset.bisDrawer !== 'true' && document.contains(form)) {
        form.dataset.bisDrawer = 'true';
      }
    }, 3000);
  }

  function scan() {
    document.querySelectorAll(FORM).forEach(function (form) {
      failSafe(form);
      decorate(form);
    });
  }

  scan();
  openOnTrigger();

  new MutationObserver(function () {
    // Cheap: the selector match is what costs, and it only runs on DOM churn.
    scan();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
